import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { fmtFechaEC } from "../utils/fecha";
import api from "../api";

async function safe(fn) { try { return await fn(); } catch { return null; } }

export default function ResellerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats,   setStats]   = useState(null);
  const [clients, setClients] = useState([]);
  const [clientTotal, setClientTotal] = useState(0);
  const [cdrs,    setCdrs]    = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [p, c] = await Promise.all([
      safe(()=>api.get("/v1/partners")),
      safe(()=>api.get("/cdr?limit=10")),
    ]);
    const partners = p?.data?.data || [];
    const myPartner = partners.find(pt => pt.name === user?.username || pt.contact_email?.includes(user?.username));
    if (myPartner) {
      // Filtro por partner_id en el SERVIDOR (antes traía los primeros 100 clientes GLOBALES y
      // filtraba en el cliente → un revendedor perdía a los suyos que caían más allá de la fila 100).
      const cl = await safe(()=>api.get("/clientes?partner_id="+myPartner.id+"&limit=500"));
      setClients(cl?.data?.data || []);
      setClientTotal(cl?.data?.total ?? (cl?.data?.data||[]).length);
    }
    setCdrs(c?.data?.data || []);
    setStats({ partner: myPartner || null });
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const activos   = clients.filter(c => c.status === "active").length;
  const suspendidos = clients.filter(c => c.status === "suspended").length;

  const fmtDate = (d) => fmtFechaEC(d);  // calldate viene en UTC → hora Ecuador
  const fmtDur  = (s) => { const n=parseInt(s)||0; if(!n)return"0s"; return n<60?n+"s":Math.floor(n/60)+"m "+n%60+"s"; };

  return (
    <div>
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Portal Revendedor</div>
          <div className="nv-page-sub">Bienvenido, {user?.username} · {new Date().toLocaleDateString("es-EC",{weekday:"long",day:"numeric",month:"long"})}</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={load} disabled={loading}>
            {loading ? <span className="nv-spinner"/> : "↺"} Actualizar
          </button>
        </div>
      </div>

      <div className="nv-kpi-grid" style={{ marginBottom:16 }}>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Mis clientes</div>
          <div className="nv-kpi-value" style={{ color:"var(--brand)" }}>{clientTotal.toLocaleString()}</div>
          <div className="nv-kpi-sub">Total asignados</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Activos</div>
          <div className="nv-kpi-value" style={{ color:"var(--success)" }}>{activos}</div>
          <div className="nv-kpi-sub">Con servicio activo</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Suspendidos</div>
          <div className="nv-kpi-value" style={{ color:"var(--warning)" }}>{suspendidos}</div>
          <div className="nv-kpi-sub">Por pago pendiente</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">CDRs recientes</div>
          <div className="nv-kpi-value" style={{ color:"var(--info)" }}>{cdrs.length}</div>
          <div className="nv-kpi-sub">Ultimas llamadas</div>
        </div>
      </div>

      <div className="nv-grid-2">
        <div className="nv-card">
          <div className="nv-card-header">
            <span className="nv-card-title">◈ Mis Clientes</span>
          </div>
          {loading ? <div className="nv-loading"><span className="nv-spinner"/></div> :
          clients.length === 0 ? (
            <div style={{ textAlign:"center",padding:"24px 0",color:"var(--text-muted)",fontSize:12 }}>Sin clientes asignados</div>
          ) : (
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead><tr><th>Cliente</th><th>Plan</th><th>Estado</th></tr></thead>
                <tbody>
                  {clients.slice(0,8).map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight:500 }}>{c.nombre || c.name || "—"}</td>
                      <td><span className="nv-badge nv-badge-info">{c.plan_nombre || "—"}</span></td>
                      <td><span className={"nv-badge "+(c.status==="active"?"nv-badge-ok":"nv-badge-muted")}>{c.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="nv-card">
          <div className="nv-card-header">
            <span className="nv-card-title">◈ Ultimas llamadas</span>
            <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={()=>navigate("/reseller-cdrs")}>Ver todas</button>
          </div>
          {loading ? <div className="nv-loading"><span className="nv-spinner"/></div> :
          cdrs.length === 0 ? (
            <div style={{ textAlign:"center",padding:"24px 0",color:"var(--text-muted)",fontSize:12 }}>Sin registros</div>
          ) : (
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead><tr><th>Fecha</th><th>Origen</th><th>Destino</th><th>Dur.</th><th>Estado</th></tr></thead>
                <tbody>
                  {cdrs.slice(0,8).map((r,i) => (
                    <tr key={i}>
                      <td style={{ fontSize:10,fontFamily:"var(--font-mono)",color:"var(--text-muted)" }}>{fmtDate(r.calldate)}</td>
                      <td className="mono">{r.src||"—"}</td>
                      <td className="mono">{r.dst||"—"}</td>
                      <td className="mono" style={{ color:"var(--brand)" }}>{fmtDur(r.billsec)}</td>
                      <td><span className={"nv-badge "+(r.disposition==="ANSWERED"?"nv-badge-ok":"nv-badge-muted")}
                        style={{ fontSize:9 }}>{r.disposition==="ANSWERED"?"✓":"x"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {stats?.partner && (
        <div className="nv-card" style={{ marginTop:14 }}>
          <div className="nv-card-header"><span className="nv-card-title">◈ Mi cuenta de revendedor</span></div>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8 }}>
            {[
              { lbl:"Nombre",   val:stats.partner.name },
              { lbl:"Estado",   val:stats.partner.status },
              { lbl:"Desde",    val:fmtDate(stats.partner.created_at) },
            ].map(({lbl,val})=>(
              <div key={lbl} style={{ background:"var(--bg-raised)",borderRadius:"var(--r-sm)",padding:"8px 12px" }}>
                <div style={{ fontSize:9,textTransform:"uppercase",letterSpacing:".07em",color:"var(--text-muted)",marginBottom:2 }}>{lbl}</div>
                <div style={{ fontSize:12,fontWeight:600,color:"var(--text-primary)" }}>{val||"—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
